/*
 * Push (docs/13 §2.5). On the website: web push, on Android browsers and on iPhone once the site is on the home screen.
 * In the iPhone app: native push (@capacitor/push-notifications); the device token is registered as an 'fcm' subscription
 * (or 'apns' until Firebase is set up in the app). Either way the phone's permission box only ever follows our own
 * notifications screen (lib/notifications.ts; Saif, 25 Oct 2026: one ask, naming offers), never on first open, and the
 * screen's wording travels with the registration so the server records both push consents with it. Subscriptions are per
 * device, stored against the customer.
 */
import { PushNotifications } from '@capacitor/push-notifications';
import { api } from './api.js';
import { track } from './events.js';
import { NATIVE, PLATFORM } from './platform.js';

const PROMPT_KEY = 'tikkas-push-prompt';
/** The app's registered device token (as the server's endpoint), so "Turn off" knows which one to forget. */
const NATIVE_KEY = 'tikkas-native-push';

/* ---------- native (the iPhone app) ---------- */

let nativePerm: PushState = 'default';
function mapPerm(p: string): PushState {
  return p === 'granted' ? 'granted' : p === 'denied' ? 'denied' : 'default';
}
/** Ask iOS what the app's notification permission is now (it can change in Settings while the app is closed). */
export async function refreshPushState(): Promise<PushState> {
  if (!NATIVE) return pushState();
  try {
    nativePerm = mapPerm((await PushNotifications.checkPermissions()).receive);
  } catch {
    nativePerm = 'unsupported';
  }
  return nativePerm;
}
if (NATIVE) refreshPushState().catch(() => undefined);

function savedNativeEndpoint(): string | null {
  try {
    return localStorage.getItem(NATIVE_KEY);
  } catch {
    return null;
  }
}

/** register() and wait for the token iOS (or Firebase) hands back. */
function nativeToken(): Promise<string> {
  return new Promise((resolve, reject) => {
    let done = false;
    const handles: { remove: () => Promise<void> }[] = [];
    const finish = (fn: () => void) => {
      if (done) return;
      done = true;
      for (const h of handles) h.remove().catch(() => undefined);
      fn();
    };
    Promise.all([
      PushNotifications.addListener('registration', (t) => finish(() => resolve(t.value))),
      PushNotifications.addListener('registrationError', (e) => finish(() => reject(new Error(e.error)))),
    ]).then((hs) => {
      handles.push(...hs);
      if (done) for (const h of hs) h.remove().catch(() => undefined);
      return PushNotifications.register();
    }).catch((e: unknown) => finish(() => reject(e instanceof Error ? e : new Error(String(e)))));
    setTimeout(() => finish(() => reject(new Error('No push token from iOS'))), 15_000);
  });
}

/** The consent block our notifications screen sends with Turn on: the wording shown, and whether it switches offers on. */
export interface AskConsent { wording: string; offers: boolean }

async function registerNative(consent: AskConsent | null): Promise<void> {
  const token = await nativeToken();
  const r = await api<{ ok: boolean; endpoint: string }>('/api/me/push/native', { body: { token, platform: PLATFORM, ...(consent ? { consent } : {}) } });
  try {
    localStorage.setItem(NATIVE_KEY, r.endpoint);
  } catch {
    /* nothing to remember with */
  }
}

/* ---------- the website (web push) ---------- */

export function pushSupported(): boolean {
  if (NATIVE) return true;
  return typeof window !== 'undefined' && 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window && location.protocol === 'https:';
}
export function isIphone(): boolean {
  return /iPhone|iPad|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
}
/** iPhone only gets web push from the home-screen app, not from Safari itself. */
export function needsHomeScreen(): boolean {
  if (NATIVE) return false;
  return isIphone() && !(window.matchMedia('(display-mode: standalone)').matches || (navigator as { standalone?: boolean }).standalone === true);
}
export type PushState = 'unsupported' | 'needs_home_screen' | 'default' | 'granted' | 'denied';
export function pushState(): PushState {
  if (NATIVE) return nativePerm;
  if (!pushSupported()) return needsHomeScreen() ? 'needs_home_screen' : 'unsupported';
  if (needsHomeScreen()) return 'needs_home_screen';
  return Notification.permission;
}

function keyBytes(b64url: string): Uint8Array {
  const pad = '='.repeat((4 - (b64url.length % 4)) % 4);
  const bin = atob((b64url + pad).replace(/-/g, '+').replace(/_/g, '/'));
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

async function registration(): Promise<ServiceWorkerRegistration> {
  const reg = await navigator.serviceWorker.getRegistration();
  return reg ?? (await navigator.serviceWorker.register('/sw.js'));
}

export type SubscribeResult = 'subscribed' | 'denied' | 'unsupported' | 'not_configured' | 'failed';

/**
 * Ask for permission (if not yet decided) and register this device. `consent` is sent from our notifications screen: the
 * server then records order updates and offers by push with that wording. Without it (Account's Turn on, after the
 * customer has already chosen there) nothing about consent changes.
 */
export async function subscribePush(opts: { consent?: AskConsent } = {}): Promise<SubscribeResult> {
  const consent = opts.consent ?? null;
  if (NATIVE) {
    try {
      // iOS shows its own "Allow notifications?" once; after that this answers at once with the saved choice.
      const perm = mapPerm((await PushNotifications.requestPermissions()).receive);
      nativePerm = perm;
      track('push_permission', { granted: perm === 'granted' });
      if (perm !== 'granted') return 'denied';
      await registerNative(consent);
      try {
        localStorage.setItem(PROMPT_KEY, 'done');
      } catch {
        /* private mode */
      }
      return 'subscribed';
    } catch {
      return 'failed';
    }
  }
  if (!pushSupported() || needsHomeScreen()) return 'unsupported';
  try {
    const v = await api<{ publicKey: string | null; configured: boolean }>('/api/push/vapid');
    if (!v.configured || !v.publicKey) return 'not_configured';
    const perm = await Notification.requestPermission();
    track('push_permission', { granted: perm === 'granted' });
    if (perm !== 'granted') return 'denied';
    const reg = await registration();
    await navigator.serviceWorker.ready;
    const sub = (await reg.pushManager.getSubscription()) ?? (await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: keyBytes(v.publicKey) as BufferSource }));
    const j = sub.toJSON();
    await api('/api/me/push/subscribe', { body: { endpoint: j.endpoint, keys: j.keys, ...(consent ? { consent } : {}) } });
    try {
      localStorage.setItem(PROMPT_KEY, 'done');
    } catch {
      /* private mode */
    }
    return 'subscribed';
  } catch {
    return 'failed';
  }
}

/** Forget this device on the server and in the browser. */
export async function unsubscribePush(): Promise<void> {
  if (NATIVE) {
    const endpoint = savedNativeEndpoint();
    if (endpoint) await api('/api/me/push/unsubscribe', { body: { endpoint } }).catch(() => undefined);
    try {
      localStorage.removeItem(NATIVE_KEY);
    } catch {
      /* nothing saved */
    }
    await PushNotifications.unregister().catch(() => undefined);
    return;
  }
  if (!pushSupported()) return;
  try {
    const reg = await registration();
    const sub = await reg.pushManager.getSubscription();
    if (sub) {
      await api('/api/me/push/unsubscribe', { body: { endpoint: sub.endpoint } }).catch(() => undefined);
      await sub.unsubscribe();
    }
  } catch {
    /* nothing to forget */
  }
}

/** After a sign-in on this device: if the browser already has a subscription, make sure the server ties it to this customer. */
export async function syncPush(): Promise<boolean> {
  if (NATIVE) {
    // Tokens can change (a restore, a reinstall): re-register quietly when notifications are already allowed. Never prompts.
    if ((await refreshPushState()) !== 'granted') return false;
    try {
      await registerNative(null);
      return true;
    } catch {
      return false;
    }
  }
  if (!pushSupported() || Notification.permission !== 'granted') return false;
  try {
    const reg = await registration();
    const sub = await reg.pushManager.getSubscription();
    if (!sub) return false;
    const j = sub.toJSON();
    await api('/api/me/push/subscribe', { body: { endpoint: j.endpoint, keys: j.keys } });
    return true;
  } catch {
    return false;
  }
}

/** Has this device got a live subscription? */
export async function hasSubscription(): Promise<boolean> {
  if (NATIVE) return (await refreshPushState()) === 'granted' && !!savedNativeEndpoint();
  if (!pushSupported()) return false;
  try {
    const reg = await navigator.serviceWorker.getRegistration();
    return !!(await reg?.pushManager.getSubscription());
  } catch {
    return false;
  }
}

/** The one-time offers question on a phone that had already allowed notifications: record the choice with its wording. */
export async function recordOffersChoice(consent: AskConsent): Promise<void> {
  await api('/api/me/notifications/choice', { body: consent });
}

/** Not now on our screen: the server notes when (it records no consent); the phone's box is never opened. */
export async function notNow(kind: 'ask' | 'offers', place: 'home' | 'status'): Promise<void> {
  await api('/api/me/notifications/not-now', { body: { kind, place } }).catch(() => undefined);
  try {
    localStorage.setItem(PROMPT_KEY, 'later');
  } catch {
    /* private mode */
  }
}
